import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { CampoControlado } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, api, get, getTodos, mensagemDeErro } from '../../nucleo/api';
import { data, dinheiro, email, nomePessoa, telefoneFormatado } from '../../nucleo/formato';
import { useSessaoFluxo } from './AvisoModo';
import { Linha, Secao } from './comum';
import { TabelaPrecos } from './TabelaPrecos';
import { COR_PEDIDO, invalidarCompras, type FornecedorDetalhe } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { rotuloForma, rotuloNomeForma } from '../../nucleo/forma-pagamento';

/** Ficha do fornecedor para Compras (M10.2 e M10.4): condição de pagamento e prazo padrão, últimos pedidos e histórico de preços. */
export function DetalheFornecedor({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const q = useQuery({ queryKey: ['/compras/fornecedores', 'obter', id], queryFn: () => get<FornecedorDetalhe>(`/compras/fornecedores/${id}`), enabled: !!id });
  const d = q.data;
  return (
    <Drawer aberto={!!id} aoFechar={aoFechar} largura="lg" titulo={d ? nomePessoa(d.nome) : t('fornecedor.tituloCurto')} rodape={<Botao onClick={aoFechar}>{tn('comum.fechar')}</Botao>}>
      {q.isPending && !!id && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {d && <Corpo d={d} />}
    </Drawer>
  );
}

function Corpo({ d }: { d: FornecedorDetalhe }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const toast = useToast();
  const qc = useQueryClient();
  const [condicao, setCondicao] = useState(d.condicao_pagamento ?? '');
  const [prazo, setPrazo] = useState<number | null>(d.prazo_entrega_dias);
  const [observacao, setObservacao] = useState(d.observacao_condicao ?? '');
  // Fase 2 (W25, K14): forma padrão do catálogo e pedido mínimo; o dado mora na ficha do Cadastros (exige cadastros.editar).
  const [forma, setForma] = useState(d.forma_pagamento_id ?? '');
  const [minimo, setMinimo] = useState<number | null>(d.pedido_minimo_centavos);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(erros, erro);
  const podeEditar = s.pode('compras.editar') && s.pode('cadastros.editar');
  const formas = useQuery({
    queryKey: ['/cadastros/formas-pagamento', 'ativas'],
    queryFn: () => getTodos<{ id: string; nome: string; tipo?: string; abreviatura?: string | null }>('/cadastros/formas-pagamento', { ativa: true }),
    enabled: s.instalado('cadastros') && s.pode('cadastros.ver'),
    staleTime: 30_000,
  });
  const opcoesForma = [
    ...(formas.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: rotuloForma(f) })),
    ...(d.forma_pagamento_id && !(formas.data?.itens ?? []).some((f) => f.id === d.forma_pagamento_id) ? [{ valor: d.forma_pagamento_id, rotulo: rotuloNomeForma(d.forma_pagamento_nome) }] : []),
  ];

  const salvar = useMutation({
    mutationFn: () =>
      api('PUT', `/compras/fornecedores/${d.id}/condicoes`, {
        condicao_pagamento: condicao.trim() || 'avista',
        prazo_entrega_dias: prazo,
        observacao: observacao.trim() || null,
        forma_pagamento_id: forma || null,
        pedido_minimo_centavos: minimo,
      }),
    onSuccess: async () => {
      await invalidarCompras(qc);
      await qc.invalidateQueries({ queryKey: ['cadastros', 'fornecimento', d.id] });
      setErros({});
      setErro(null);
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {d.a_receber > 0 && <Selo cor="ciano">{t('fornecedor.pedidosAReceber', { count: d.a_receber })}</Selo>}
      </div>
      <p className="numero mb-1 text-3xl font-semibold text-tinta-1">{anunciarOcultos(dinheiro(d.comprado_centavos))}</p>
      <p className="mb-4 text-sm text-tinta-3">{t('fornecedor.totalComprado')}</p>
      <dl className="divide-y divide-borda">
        <Linha rotulo={t('campos.fornecedor')}>
          <Link to={`/cadastros/cadastros/parceiro/${d.id}`} className="text-acento-forte hover:underline">
            {nomePessoa(d.nome)}
          </Link>
        </Linha>
        {d.email && <Linha rotulo={t('campos.email')}>{email(d.email)}</Linha>}
        {d.telefone && <Linha rotulo={t('campos.telefone')}>{telefoneFormatado(d.telefone)}</Linha>}
        <Linha rotulo={t('campos.pedidos')}>{d.pedidos}</Linha>
        {d.ultimo_pedido && <Linha rotulo={t('campos.ultimoPedido')}>{data(d.ultimo_pedido)}</Linha>}
      </dl>

      <Secao titulo={t('fornecedor.condicoesTitulo')}>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            salvar.mutate();
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <p className="text-sm text-tinta-3 sm:col-span-2">
            {t('fornecedor.condicoesExplicacao')} {t('w25.condicoesNaFicha')}
          </p>
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-2">
              {erro}
            </p>
          )}
          <CampoControlado def={{ nome: 'condicao_pagamento', rotulo: t('campos.condicao'), tipo: 'texto', placeholder: '30/60/90', ajuda: t('ajuda.condicao'), desabilitado: !podeEditar }} valor={condicao} aoMudar={(v) => setCondicao(String(v ?? ''))} erro={erros.condicao_pagamento} />
          <CampoControlado def={{ nome: 'prazo_entrega_dias', rotulo: t('campos.prazoEntregaDias'), tipo: 'numero', min: 0, max: 365, passo: 1, ajuda: t('ajuda.prazoPadrao'), desabilitado: !podeEditar }} valor={prazo} aoMudar={(v) => setPrazo(v === null || v === '' ? null : Number(v))} erro={erros.prazo_entrega_dias} />
          <CampoControlado def={{ nome: 'forma_pagamento_id', rotulo: t('w25.forma'), tipo: 'selecao', opcoes: opcoesForma, desabilitado: !podeEditar }} valor={forma} aoMudar={(v) => setForma(String(v ?? ''))} erro={erros.forma_pagamento_id} />
          <CampoControlado def={{ nome: 'pedido_minimo_centavos', rotulo: t('w25.minimo'), tipo: 'dinheiro', desabilitado: !podeEditar }} valor={minimo} aoMudar={(v) => setMinimo((v as number | null) ?? null)} erro={erros.pedido_minimo_centavos} />
          <div className="sm:col-span-2">
            <CampoControlado def={{ nome: 'observacao', rotulo: t('campos.observacao'), tipo: 'texto', desabilitado: !podeEditar }} valor={observacao} aoMudar={(v) => setObservacao(String(v ?? ''))} erro={erros.observacao} />
          </div>
          {podeEditar && (
            <div className="flex justify-end sm:col-span-2">
              <Botao variante="primario" type="submit" carregando={salvar.isPending}>
                {tn('comum.salvar')}
              </Botao>
            </div>
          )}
        </form>
      </Secao>

      <Secao titulo={t('fornecedor.precosTitulo')}>
        <TabelaPrecos linhas={d.precos} semFornecedor />
      </Secao>

      <Secao titulo={t('fornecedor.ultimosPedidos')}>
        {d.ultimos_pedidos.length === 0 ? (
          <p className="text-sm text-tinta-3">{t('fornecedor.semPedidos')}</p>
        ) : (
          <ul className="divide-y divide-borda rounded-lg border border-borda">
            {d.ultimos_pedidos.map((p) => (
              <li key={p.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2 text-sm">
                <Link to={`/operacao/compras/pedidos?pedido=${p.id}`} className="min-w-0 text-acento-forte hover:underline">
                  <TextoCortado className="block">
                    {p.numero}
                  </TextoCortado>
                </Link>
                <span className="flex shrink-0 items-center gap-3">
                  <Selo cor={COR_PEDIDO[p.estado]}>{t(`estadoPedido.${p.estado}`)}</Selo>
                  <span className="numero">{anunciarOcultos(dinheiro(p.total_centavos, p.moeda))}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Secao>

      <div className="mt-6 border-t border-borda pt-4">
        <Chatter entidade="compras.fornecedor" registroId={d.id} titulo={d.nome} rotulosCampos={{ condicao_pagamento: t('campos.condicao'), prazo_entrega_dias: t('campos.prazoEntregaDias'), observacao: t('campos.observacao'), forma_pagamento: t('w25.forma'), pedido_minimo_centavos: t('w25.minimo') }} />
      </div>
    </>
  );
}
